import { useEffect } from 'react';
import { useLegalSettings, orPlaceholder } from '../hooks/useLegalSettings';

export default function Legal() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { settings, loading } = useLegalSettings();

  const s = settings || {};
  const updateLabel = s.updatedAtLabel || '—';
  const email = s.email || 'contact@pernetyjewelry.com';

  return (
    <div>
      {/* ── HERO ── */}
      <section className="relative h-[40vh] md:h-[50vh] flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.5)' }} />
        <div className="relative z-10 text-center px-6">
          <div className="flex items-center justify-center gap-6 mb-8">
            <div className="w-12 h-px bg-white/30" />
            <img src="/photos/Logo_fd_transparent.png" alt="" className="h-10 w-auto"
              style={{ filter: 'invert(1) brightness(2)', opacity: 0.5 }} />
            <div className="w-12 h-px bg-white/30" />
          </div>
          <h1 className="text-white font-light" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(36px, 5vw, 60px)'
          }}>
            Mentions légales
          </h1>
        </div>
      </section>

      {/* ── CONTENU ── */}
      <section className="bg-white py-20 md:py-24 px-6">
        <div className="max-w-3xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>

          <p className="text-xs uppercase tracking-[0.25em] text-gray-400 mb-12 text-center">
            Dernière mise à jour : {updateLabel}
          </p>

          {loading ? (
            <p className="text-center text-gray-400">Chargement...</p>
          ) : (
          <>

          {/* Éditeur */}
          <Block title="1. Éditeur du site">
            <p>
              Le site <strong>pernetyparis.com</strong> est édité par :
            </p>
            <ul className="space-y-1 my-4">
              <li><strong>Nom commercial :</strong> Pernety</li>
              <li><strong>Responsable :</strong> {orPlaceholder(s.legalName, 'Prénom NOM')}</li>
              <li><strong>Statut juridique :</strong> {orPlaceholder(s.legalStatus, 'ex. Entrepreneur individuel (EI)')}</li>
              <li><strong>Adresse du siège :</strong> {orPlaceholder(s.address, 'adresse complète')}</li>
              <li><strong>Numéro SIRET :</strong> {orPlaceholder(s.siret, '14 chiffres')}</li>
              <li><strong>Code APE/NAF :</strong> {orPlaceholder(s.apeCode, 'ex. 32.12Z')}</li>
              <li><strong>Numéro de TVA intracommunautaire :</strong> {orPlaceholder(s.vatNumber, 'numéro ou « Non assujettie — art. 293 B du CGI »')}</li>
              {s.capital && s.capital.trim() && (
                <li><strong>Capital social :</strong> {s.capital}</li>
              )}
              {s.rcs && s.rcs.trim() && (
                <li><strong>RCS :</strong> {s.rcs}</li>
              )}
              <li><strong>Téléphone :</strong> {orPlaceholder(s.phone, 'numéro de téléphone')}</li>
              <li><strong>Email :</strong> {email}</li>
            </ul>
          </Block>

          {/* Directeur publication */}
          <Block title="2. Directeur de la publication">
            <p>
              Le directeur de la publication du site est {orPlaceholder(s.legalName, 'Prénom NOM')}, en sa qualité de {orPlaceholder(s.publicationManagerRole, 'ex. fondatrice')}.
            </p>
          </Block>

          {/* Hébergement */}
          <Block title="3. Hébergement">
            <p>Le site est hébergé par :</p>
            <ul className="space-y-1 my-4">
              <li><strong>Société :</strong> OVH SAS</li>
              <li><strong>Adresse :</strong> 2 rue Kellermann, 59100 Roubaix, France</li>
              <li><strong>Téléphone :</strong> +33 (0)9 72 10 10 07</li>
              <li><strong>Site web :</strong> <a href="https://www.ovhcloud.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-black">www.ovhcloud.com</a></li>
            </ul>
          </Block>

          {/* Propriété intellectuelle */}
          <Block title="4. Propriété intellectuelle">
            <p>
              L'ensemble des éléments présents sur le site (textes, photographies, illustrations, graphismes, logos, marques, mises en page, créations, vidéos, sons et plus généralement tout contenu) est la propriété exclusive de Pernety ou de ses partenaires et est protégé par le droit français et international relatif à la propriété intellectuelle.
            </p>
            <p>
              Toute reproduction, représentation, modification, publication, transmission, dénaturation, totale ou partielle, du site ou de son contenu, par quelque procédé que ce soit, et sur quelque support que ce soit, est interdite sans autorisation écrite préalable de Pernety. Toute exploitation non autorisée constituerait une contrefaçon sanctionnée par les articles L.335-2 et suivants du Code de la propriété intellectuelle.
            </p>
            <p>
              La marque « Pernety » ainsi que le logo associé sont la propriété de {orPlaceholder(s.trademarkOwner, 'titulaire des droits + n° INPI si applicable')}.
            </p>
          </Block>

          {/* Crédits */}
          <Block title="5. Crédits">
            <ul className="space-y-1 my-4">
              <li><strong>Conception et développement :</strong> {s.creditsDeveloper || 'Webcraft'}</li>
              <li><strong>Photographies :</strong> {orPlaceholder(s.creditsPhotographer, 'nom du/des photographe(s)')}</li>
              {s.creditsDesigner && s.creditsDesigner.trim() && (
                <li><strong>Identité graphique :</strong> {s.creditsDesigner}</li>
              )}
            </ul>
          </Block>

          {/* Responsabilité */}
          <Block title="6. Limitation de responsabilité">
            <p>
              Pernety s'efforce d'assurer au mieux de ses possibilités l'exactitude et la mise à jour des informations diffusées sur le site. Toutefois, Pernety ne peut garantir l'exactitude, la précision ou l'exhaustivité des informations mises à disposition sur le site.
            </p>
            <p>
              En conséquence, Pernety décline toute responsabilité pour toute imprécision, inexactitude ou omission portant sur des informations disponibles sur le site, ainsi que pour tout dommage résultant d'une intrusion frauduleuse d'un tiers.
            </p>
            <p>
              L'utilisateur reconnaît utiliser le site sous sa seule et entière responsabilité.
            </p>
          </Block>

          {/* Liens */}
          <Block title="7. Liens hypertextes">
            <p>
              Le site peut contenir des liens hypertextes vers d'autres sites. Pernety n'exerce aucun contrôle sur ces sites et n'assume aucune responsabilité quant à leur contenu, à leur disponibilité ou aux pratiques qu'ils mettent en œuvre concernant les données personnelles.
            </p>
            <p>
              La création de liens vers le site nécessite l'autorisation préalable et écrite de Pernety.
            </p>
          </Block>

          {/* Données personnelles */}
          <Block title="8. Données personnelles">
            <p>
              Le traitement des données à caractère personnel collectées sur le site est détaillé dans la <a href="/politique-confidentialite" className="underline hover:text-black">Politique de confidentialité</a>, qui fait partie intégrante des présentes mentions légales.
            </p>
          </Block>

          {/* Loi & juridiction */}
          <Block title="9. Loi applicable et juridiction compétente">
            <p>
              Les présentes mentions légales sont soumises au droit français. En cas de litige relatif à l'utilisation du site, et à défaut de résolution amiable, les tribunaux français seront seuls compétents.
            </p>
          </Block>

          {/* Contact */}
          <Block title="10. Contact">
            <p>
              Pour toute question relative aux présentes mentions légales, vous pouvez nous écrire à l'adresse <a href={`mailto:${email}`} className="underline hover:text-black">{email}</a> ou via le <a href="/contact" className="underline hover:text-black">formulaire de contact</a>.
            </p>
          </Block>

          </>
          )}

        </div>
      </section>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="mb-12">
      <h2 className="font-light mb-5 text-gray-900" style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 'clamp(20px, 1.8vw, 26px)'
      }}>
        {title}
      </h2>
      <div className="space-y-4 text-[14.5px] leading-[1.85]" style={{ color: '#4a4a4a' }}>
        {children}
      </div>
    </div>
  );
}
